import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Typography
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import codexTypeService from '../services/codexTypeService';
import processService from '../services/processService';
import api from '../services/apiConfig';
import { avvisoPerTipo, useMyCodexTypes } from './codex/CodexTypeScopeWarning';

const overlayStyles = {
  position: 'fixed',
  inset: 0,
  zIndex: (theme) => theme.zIndex.modal + 5,
  bgcolor: 'rgba(255, 255, 255, 0.96)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  p: { xs: 2, md: 6 },
};

const gridStyles = {
  width: '100%',
  maxWidth: 960,
  mt: 4,
};

const dropCardStyles = (isActive) => ({
  position: 'relative',
  border: '2px dashed',
  borderColor: isActive ? 'primary.main' : 'divider',
  bgcolor: isActive ? 'primary.light' : 'background.paper',
  color: isActive ? 'primary.contrastText' : 'text.primary',
  p: 3,
  textAlign: 'center',
  height: '100%',
  transition: 'all 0.2s ease',
  cursor: 'copy',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 1,
  '&:hover': {
    borderColor: 'primary.main',
    boxShadow: 3,
  },
});

const progressBoxStyles = {
  position: 'absolute',
  inset: 0,
  bgcolor: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: 2,
  color: '#fff',
  p: 3,
};

const GlobalDropOverlay = ({
  open,
  onRequestClose,
  defaultOwner = '',
  onCompleted,
}) => {
  const myCodexTypes = useMyCodexTypes();
  const [codexTypes, setCodexTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [error, setError] = useState(null);
  const [activeTypeId, setActiveTypeId] = useState(null);
  const [uploadState, setUploadState] = useState({ status: 'idle', processed: 0, total: 0, typeId: null });

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    const loadTypes = async () => {
      try {
        setLoadingTypes(true);
        setError(null);
        const types = await codexTypeService.getAll();
        if (mounted) {
          setCodexTypes(types || []);
        }
      } catch (err) {
        console.error('Unable to load codex types for drop overlay', err);
        if (mounted) {
          setError('Impossibile recuperare i tipi di Codex. Riprova più tardi.');
        }
      } finally {
        if (mounted) {
          setLoadingTypes(false);
        }
      }
    };

    loadTypes();

    return () => {
      mounted = false;
    };
  }, [open]);

  const resetOverlay = useCallback(() => {
    setActiveTypeId(null);
    setUploadState({ status: 'idle', processed: 0, total: 0, typeId: null });
  }, []);

  const handleClose = useCallback(() => {
    if (uploadState.status === 'processing') {
      return; // avoid closing while processing upload
    }
    resetOverlay();
    onRequestClose?.();
  }, [onRequestClose, resetOverlay, uploadState.status]);

  const handleDropOnType = useCallback(async (event, codexType) => {
    event.preventDefault();
    event.stopPropagation();

    const files = Array.from(event.dataTransfer?.files || []).filter((file) => {
      if (!file) return false;
      if (file.type === 'application/pdf') {
        return true;
      }
      return file.name?.toLowerCase().endsWith('.pdf');
    });

    if (files.length === 0) {
      setError('Sono accettati solo file PDF.');
      return;
    }

    try {
      setUploadState({ status: 'processing', processed: 0, total: files.length, typeId: codexType.id });
      setError(null);

      // Determine default process for this type (first available)
      let processId = null;
      try {
        const processes = await processService.getProcessesByCodexType(codexType.id);
        processId = processes?.[0]?.id || null;
      } catch (processErr) {
        console.warn('No processes found for codex type', codexType.id, processErr);
      }

      const ownerValue = defaultOwner || '';

      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const timestamp = Date.now();
        const codexPayload = {
          codexType: codexType.id,
          code: `${codexType.code || 'DOC'}-${timestamp}-${index + 1}`,
          description: file.name,
          owner: ownerValue,
          attributeValues: {},
        };

        try {
          const { data: createdCodex } = await api.post('/codexes', codexPayload);

          if (file) {
            const formData = new FormData();
            formData.append('file', file);
            await api.post(`/codex-files?codexId=${createdCodex.id}`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          }

          if (processId) {
            await processService.startCodexProcess(createdCodex.id, processId, {});
          }
        } catch (creationErr) {
          console.error('Errore durante la creazione del Codex via drop', creationErr);
          throw creationErr;
        }

        setUploadState((prev) => ({
          ...prev,
          processed: index + 1,
        }));
      }

      setUploadState((prev) => ({ ...prev, status: 'completed' }));
      onCompleted?.();
      setTimeout(() => {
        resetOverlay();
        onRequestClose?.();
      }, 400);
    } catch (uploadErr) {
      console.error('Drop upload failed', uploadErr);
      setError('Creazione dei documenti fallita. Verifica i file e riprova.');
      setUploadState({ status: 'error', processed: 0, total: 0, typeId: null });
    }
  }, [defaultOwner, onCompleted, onRequestClose, resetOverlay]);

  const overlayContent = useMemo(() => {
    if (!open) return null;

    if (loadingTypes) {
      return (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <CircularProgress size={48} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Caricamento tipi di Codex...
          </Typography>
        </Box>
      );
    }

    if (error && codexTypes.length === 0) {
      return (
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      );
    }

    return (
      <Grid container spacing={3} sx={gridStyles}>
        {codexTypes.map((type) => {
          const isActive = activeTypeId === type.id;
          const isProcessing = uploadState.status === 'processing' && uploadState.typeId === type.id;
          const progress = uploadState.total > 0 ? Math.round((uploadState.processed / uploadState.total) * 100) : 0;

          return (
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              key={type.id}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexBasis: { md: '30%' },
                maxWidth: { md: '30%' }
              }}
            >
              <Paper
                elevation={isActive ? 6 : 2}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setActiveTypeId(type.id);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'copy';
                  setActiveTypeId(type.id);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setActiveTypeId((prev) => (prev === type.id ? null : prev));
                }}
                onDrop={(e) => handleDropOnType(e, type)}
                sx={{
                  width: { xs: '100%', md: '100%' },
                  maxWidth: { xs: '100%', md: '420px' },
                  ...dropCardStyles(isActive || isProcessing)
                }}
              >
                <Typography variant="h6" sx={{ textTransform: 'uppercase', fontWeight: 700 }}>
                  {type.code}
                </Typography>
                {type.description && (
                  <Typography variant="body2" color="inherit" sx={{ opacity: 0.8 }}>
                    {type.description}
                  </Typography>
                )}

                {/* D2: qui la scelta del tipo E' la carta su cui si lascia il file, e non
                    c'e' un «dopo» in cui avvertire — il caricamento parte al rilascio.
                    L'avviso sta quindi sulla carta, prima del gesto. Non la disabilita:
                    codex.create resta senza ambito. */}
                {avvisoPerTipo(type.id, myCodexTypes) && (
                  <Typography
                    variant="caption"
                    color="inherit"
                    sx={{ opacity: 0.85, mt: 1, display: 'block', fontStyle: 'italic' }}
                  >
                    {avvisoPerTipo(type.id, myCodexTypes)}
                  </Typography>
                )}

                {isProcessing && (
                  <Box sx={progressBoxStyles}>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{ width: '100%', maxWidth: 220, bgcolor: 'rgba(255,255,255,0.25)' }}
                    />
                    <Typography variant="body2">Creazione documenti {uploadState.processed}/{uploadState.total}</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    );
  }, [open, loadingTypes, error, codexTypes, activeTypeId, uploadState, handleDropOnType, myCodexTypes]);

  if (!open) {
    return null;
  }

  return (
    <Box
      sx={overlayStyles}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleClose();
      }}
    >
      <IconButton
        onClick={handleClose}
        sx={{ position: 'absolute', top: { xs: 8, md: 24 }, right: { xs: 8, md: 24 } }}
        color="inherit"
      >
        <CloseIcon />
      </IconButton>

      {error && (
        <Alert severity="error" sx={{ mt: 3, maxWidth: 480 }}>
          {error}
        </Alert>
      )}

      {overlayContent}
    </Box>
  );
};

export default GlobalDropOverlay;
